type HistoryPayload = unknown;

export interface HistoryEntry {
  id: number;
  label: string;
  timestamp: number;
  actions: HistoryPayload[];
  undoActions: HistoryPayload[];
}

type HistoryReplayer = (payload: HistoryPayload) => unknown;
type HistoryChangeListener = () => void;
type ActionMerger = (existing: HistoryPayload[], incoming: HistoryPayload[]) => HistoryPayload[] | null;

export interface HistoryState {
  entries: HistoryEntry[];
  currentIndex: number;
  nextEntryId: number;
  pendingLabel: string | null;
  pendingActions: HistoryPayload[];
  pendingUndoActions: HistoryPayload[];
  replayDepth: number;
}

export interface HistoryMutator {
  <T>(label: string, fn: () => T): T;
  begin(label: string): void;
  commit(): void;
  discard(): void;
}

export interface HistoryStore {
  history: HistoryState;
  setActionMerger(merger: ActionMerger): void;
  setHistoryReplayer(replayer: HistoryReplayer): void;
  subscribeHistoryChanged(listener: HistoryChangeListener): () => void;
  pushHistoryEntry(label: string, actions: HistoryPayload[], undoActions: HistoryPayload[]): void;
  mutate: HistoryMutator;
  performUndo(notify?: boolean): boolean;
  performRedo(notify?: boolean): boolean;
  jumpToHistory(index: number): boolean;
  resetHistory(label?: string): void;
}

export function createHistoryState(): HistoryState {
  return {
    entries: [],
    currentIndex: -1,
    nextEntryId: 1,
    pendingLabel: null,
    pendingActions: [],
    pendingUndoActions: [],
    replayDepth: 0,
  };
}

function clonePayload<T>(payload: T): T {
  return structuredClone(payload);
}

function clonePayloadList(payloads: HistoryPayload[]): HistoryPayload[] {
  return payloads.map(clonePayload);
}

export function createHistoryStore(history: HistoryState = createHistoryState()): HistoryStore {
  let historyReplayer: HistoryReplayer | null = null;
  let actionMerger: ActionMerger | null = null;
  const historyChangeListeners = new Set<HistoryChangeListener>();

  function notifyHistoryChanged(): void {
    for (const listener of historyChangeListeners) {
      listener();
    }
  }

  function ensureHistoryInitialized(): void {
    if (history.entries.length > 0) return;
    history.entries.push({
      id: history.nextEntryId++,
      label: 'Initial state',
      timestamp: Date.now(),
      actions: [],
      undoActions: [],
    });
    history.currentIndex = 0;
  }

  function truncateFutureHistory(): void {
    if (history.currentIndex >= history.entries.length - 1) return;
    history.entries = history.entries.slice(0, history.currentIndex + 1);
  }

  function appendHistoryEntry(label: string, actions: HistoryPayload[], undoActions: HistoryPayload[]): void {
    if (actions.length === 0) return;
    truncateFutureHistory();
    history.entries.push({
      id: history.nextEntryId++,
      label,
      timestamp: Date.now(),
      actions: clonePayloadList(actions),
      undoActions: clonePayloadList(undoActions),
    });
    history.currentIndex = history.entries.length - 1;
  }

  function clearPendingEntry(): void {
    history.pendingLabel = null;
    history.pendingActions = [];
    history.pendingUndoActions = [];
  }

  function startPendingEntry(label: string): void {
    ensureHistoryInitialized();
    if (history.pendingLabel) {
      finalizePendingEntry();
    }
    history.pendingLabel = label;
    history.pendingActions = [];
    history.pendingUndoActions = [];
  }

  function pushHistoryEntry(label: string, actions: HistoryPayload[], undoActions: HistoryPayload[]): void {
    if (history.replayDepth > 0) return;
    ensureHistoryInitialized();

    const nextActions = clonePayloadList(actions);
    const nextUndoActions = clonePayloadList(undoActions);

    if (history.pendingLabel) {
      const merged = actionMerger?.(history.pendingActions, nextActions) ?? null;
      if (merged !== null) {
        history.pendingActions = merged;
        // Keep existing pendingUndoActions: the first undo already captures the original state.
      } else {
        history.pendingActions.push(...nextActions);
        history.pendingUndoActions = [...nextUndoActions, ...history.pendingUndoActions];
      }
      return;
    }

    appendHistoryEntry(label, nextActions, nextUndoActions);
    notifyHistoryChanged();
  }

  function finalizePendingEntry(): boolean {
    ensureHistoryInitialized();
    const label = history.pendingLabel;
    const actions = history.pendingActions;
    const undoActions = history.pendingUndoActions;
    clearPendingEntry();

    if (!label || actions.length === 0) return false;
    appendHistoryEntry(label, actions, undoActions);
    return true;
  }

  function replayActions(actions: HistoryPayload[]): void {
    if (!historyReplayer) {
      throw new Error('History replayer is not configured');
    }

    history.replayDepth++;
    try {
      for (const action of actions) {
        historyReplayer(clonePayload(action));
      }
    } finally {
      history.replayDepth--;
    }
  }

  function rollbackPendingEntry(): boolean {
    ensureHistoryInitialized();
    const hasPendingChanges = history.pendingActions.length > 0;
    const undoActions = clonePayloadList(history.pendingUndoActions);
    clearPendingEntry();
    if (!hasPendingChanges) return false;
    replayActions(undoActions);
    return true;
  }

  const mutate: HistoryMutator = Object.assign(
    function mutate<T>(label: string, fn: () => T): T {
      ensureHistoryInitialized();
      const alreadyPending = !!history.pendingLabel;
      if (!alreadyPending) {
        startPendingEntry(label);
      }

      try {
        return fn();
      } finally {
        if (!alreadyPending && finalizePendingEntry()) {
          notifyHistoryChanged();
        }
      }
    },
    {
      begin(label: string): void {
        startPendingEntry(label);
      },
      commit(): void {
        if (finalizePendingEntry()) {
          notifyHistoryChanged();
        }
      },
      discard(): void {
        rollbackPendingEntry();
      },
    },
  );

  function performUndo(notify = true): boolean {
    ensureHistoryInitialized();
    rollbackPendingEntry();
    if (history.currentIndex <= 0) return false;

    const entry = history.entries[history.currentIndex];
    replayActions(entry.undoActions);
    history.currentIndex--;
    if (notify) notifyHistoryChanged();
    return true;
  }

  function performRedo(notify = true): boolean {
    ensureHistoryInitialized();
    rollbackPendingEntry();
    if (history.currentIndex >= history.entries.length - 1) return false;

    const entry = history.entries[history.currentIndex + 1];
    replayActions(entry.actions);
    history.currentIndex++;
    if (notify) notifyHistoryChanged();
    return true;
  }

  function jumpToHistory(index: number): boolean {
    ensureHistoryInitialized();
    const rolledBackPending = rollbackPendingEntry();
    if (index < 0 || index >= history.entries.length) return false;
    if (index === history.currentIndex) {
      if (rolledBackPending) notifyHistoryChanged();
      return rolledBackPending;
    }

    if (index < history.currentIndex) {
      while (history.currentIndex > index) {
        performUndo(false);
      }
    } else {
      while (history.currentIndex < index) {
        performRedo(false);
      }
    }

    notifyHistoryChanged();
    return true;
  }

  function resetHistory(label = 'Current state'): void {
    history.entries = [{
      id: history.nextEntryId++,
      label,
      timestamp: Date.now(),
      actions: [],
      undoActions: [],
    }];
    history.currentIndex = 0;
    clearPendingEntry();
    notifyHistoryChanged();
  }

  return {
    history,
    setActionMerger(merger) {
      actionMerger = merger;
    },
    setHistoryReplayer(replayer) {
      historyReplayer = replayer;
    },
    subscribeHistoryChanged(listener) {
      historyChangeListeners.add(listener);
      return () => {
        historyChangeListeners.delete(listener);
      };
    },
    pushHistoryEntry,
    mutate,
    performUndo,
    performRedo,
    jumpToHistory,
    resetHistory,
  };
}
